import type { AxiosError } from 'axios';
import axios, { type InternalAxiosRequestConfig } from 'axios';
import { env } from './env';
import { getTokenStore } from './token-store';

export const api = axios.create({
  baseURL: `${env.API_URL}${env.API_PREFIX.startsWith('/') ? env.API_PREFIX : `/${env.API_PREFIX}`}`,
  withCredentials: true,
  timeout: 15_000,
});

api.interceptors.request.use((config) => {
  const token = getTokenStore().getAccessToken();
  const scope = getActiveAuthScope();
  config.headers = config.headers ?? {};
  (config.headers as Record<string, string>)['x-ecregi-scope'] = scope;
  if (token && token !== 'cookie-session') {
    (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;
  }
  return config;
});

export type AuthScope = 'admin' | 'associate' | 'client';

export function getActiveAuthScope(): AuthScope {
  if (typeof window !== 'undefined' && (window as any).__ECREGI_APP_ROLE === 'admin') {
    return 'admin';
  }
  if (typeof window !== 'undefined') {
    const p = window.location.pathname;
    const s = window.location.search;
    if (p.startsWith('/associate') || s.includes('as=associate')) {
      return 'associate';
    }
  }
  return 'client';
}

// --- Refresh token flow -----------------------------------------------------
// On 401 for a non-refresh request, attempt a single refresh. If another call
// is already refreshing, queue onto its promise. If refresh fails, clear
// tokens and bubble the 401 — the auth guard handles redirect.
let refreshInFlight: Promise<string | null> | null = null;

export async function refreshAccessToken(targetScope?: AuthScope): Promise<string | null> {
  const scope = targetScope ?? getActiveAuthScope();
  const apiPrefix = env.API_PREFIX.startsWith('/') ? env.API_PREFIX : `/${env.API_PREFIX}`;
  const refreshUrl = `${env.API_URL}${apiPrefix}/auth/${scope}/refresh`;

  try {
    await axios.post(
      refreshUrl,
      {},
      { withCredentials: true, timeout: 15_000, headers: { 'x-ecregi-scope': scope } },
    );
    getTokenStore().setAccessToken('cookie-session', scope);
    return 'cookie-session';
  } catch {
    getTokenStore().clear(scope);
    return null;
  }
}

api.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const original = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    const url = original?.url ?? '';
    const isRefreshCall = url.includes('/refresh');
    if (error.response?.status !== 401 || original?._retry || isRefreshCall) {
      return Promise.reject(error);
    }
    original._retry = true;
    refreshInFlight =
      refreshInFlight ??
      refreshAccessToken().finally(() => {
        refreshInFlight = null;
      });
    const newToken = await refreshInFlight;
    if (!newToken) return Promise.reject(error);
    original.headers = original.headers ?? {};
    original.headers['x-ecregi-scope'] = getActiveAuthScope();
    if (newToken !== 'cookie-session') {
      (original.headers as Record<string, string>).Authorization = `Bearer ${newToken}`;
    }
    return api.request(original);
  },
);

export type ApiError = AxiosError<{
  statusCode?: number;
  message?: string | string[];
  detail?: string;
  title?: string;
  errors?: Array<{ path?: string[]; message?: string }>;
}>;

export function apiErrorMessage(err: unknown, fallback = 'Something went wrong'): string {
  const e = err as ApiError;
  const data = e?.response?.data;
  if (!data) {
    if (e?.code === 'ECONNABORTED' || e?.message?.includes('timeout')) {
      return 'Upload timed out. Please check your internet connection and try again.';
    }
    if (e?.message && typeof e.message === 'string' && e.message !== 'Network Error') {
      return e.message;
    }
    return fallback;
  }

  if (Array.isArray(data.errors) && data.errors.length > 0) {
    const firstErr = data.errors[0];
    const pathStr = firstErr.path?.length ? `${firstErr.path.join('.')}: ` : '';
    if (firstErr.message) return `${pathStr}${firstErr.message}`;
  }

  const m = data.message;
  if (Array.isArray(m)) return m[0] ?? fallback;
  if (typeof m === 'string' && m !== 'Validation failed') return m;
  if (typeof data.detail === 'string') return data.detail;
  if (typeof data.title === 'string') return data.title;
  if (typeof m === 'string') return m;
  return fallback;
}
